import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FLIGHT_ENDPOINTS, USER_ENDPOINTS } from '../config/api';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
  IconButton,
} from '@mui/material';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import FlightLandIcon from '@mui/icons-material/FlightLand';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import PriceChart from '../components/PriceChart';
import { handleFlightCurrency } from '../utils/trackerUtils';

const TrackerDetails = () => {
  const [flightDetails, setFlightDetails] = useState({});
  const [priceHistory, setPriceHistory] = useState({});
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [error, setError] = useState('');
  const { getAuthHeader } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  dayjs.extend(utc)

  const { id } = useParams();

  useEffect(() => {
    fetchWebhooks();
    fetchFlightDetails();
    fetchPriceHistory();
  }, [id]);

  const fetchFlightDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(FLIGHT_ENDPOINTS.DETAIL(id), {
        headers: getAuthHeader(),
      });

      if (response.ok) {
        const flight = await response.json();
        setFlightDetails({
          ...flight,
          outboundDeparture: flight.outboundDeparture ? dayjs.utc(flight.outboundDeparture) : dayjs.utc(),
          outboundArrival: flight.outboundArrival ? dayjs.utc(flight.outboundArrival) : dayjs.utc(),
          inboundDeparture: flight.inboundDeparture ? dayjs.utc(flight.inboundDeparture) : null,
          inboundArrival: flight.inboundArrival ? dayjs.utc(flight.inboundArrival) : null,
        });
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch flight details');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchPriceHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await fetch(FLIGHT_ENDPOINTS.HISTORY(id), {
        headers: getAuthHeader(),
      });

      if (response.ok) {
        const flightPriceHistory = await response.json();
        setPriceHistory(flightPriceHistory);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch price history');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoadingHistory(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return dayjs.utc(dateString).format('DD/MM/YYYY, HH:mm');
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatLocalDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return dayjs.utc(dateString).local().format('DD/MM/YYYY, HH:mm');
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const fetchWebhooks = async () => {
    try {
      const response = await fetch(USER_ENDPOINTS.WEBHOOKS, {
        headers: getAuthHeader(),
      });

      if (response.ok) {
        const data = await response.json();
        setWebhooks(data.webhooks);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch webhooks');
      }
    } catch (err) {
      setError('Something went wrong while fetching webhooks');
    }
  };

  const getWebhookName = (webhookId) => {
    if (!webhookId || !Array.isArray(webhooks)) return 'No webhook';

    const webhook = webhooks.find((webhook) => webhook.id === webhookId);
    return webhook ? webhook.name : 'No webhook';
  };

  const handleEdit = (flightId) => {
    navigate(`/flights/edit/${flightId}`);
  };

  const getTapFlightUrl = (flight) => {
    const base_url = "https://booking.flytap.com/booking/flights/deeplink";

    const outbound_departure_date = dayjs.utc(flight.outboundDeparture).format('DD.MM.YYYY')


    if (flight.type === "return") {
      const inbound_departure_date = dayjs.utc(flight.inboundDeparture).format('DD.MM.YYYY')
      
      const flightUrl = `${base_url}?market=BR&language=en&origin=${flight.origin}&destination=${flight.destination}&flexibleDates=false&adt=${flight.numberOfPeople}&chd=0&inf=0&yth=0&depDate=${outbound_departure_date}&flightType=return&retDate=${inbound_departure_date}&headerfooterhidden=false&x_tap_source=WEB&x_tap_username=`

      return flightUrl
    } else {
      const flightUrl = `${base_url}?market=BR&language=en&origin=${flight.origin}&destination=${flight.destination}&flexibleDates=false&adt=${flight.numberOfPeople}&chd=0&inf=0&yth=0&depDate=${outbound_departure_date}&headerfooterhidden=false&x_tap_source=WEB&x_tap_username=`
      
      return flightUrl
    }
  }

  const getTamFlightUrl = (flight) => {
    const base_url = "https://www.latamairlines.com/br/pt/oferta-voos";

    const outbound_departure_date = dayjs.utc(flight.outboundDeparture).format('YYYY-MM-DD')


    if (flight.type === "return") {
      const trip = "RT"
      const inbound_departure_date = dayjs.utc(flight.inboundDeparture).format('YYYY-MM-DD')
      
      const flightUrl = `${base_url}?origin=${flight.origin}&outbound=${outbound_departure_date}T12%3A00%3A00.000Z&destination=${flight.destination}&inbound=${inbound_departure_date}T12%3A00%3A00.000Z&adt=${flight.numberOfPeople}&chd=0&inf=0&trip=${trip}&cabin=Economy&redemption=false&sort=RECOMMENDED`

      return flightUrl
    } else {
      const trip = "OW"
      const flightUrl = `${base_url}?origin=${flight.origin}&outbound=${outbound_departure_date}T12%3A00%3A00.000Z&destination=${flight.destination}&inbound=null&adt=${flight.numberOfPeople}&chd=0&inf=0&trip=${trip}&cabin=Economy&redemption=false&sort=RECOMMENDED`
      
      return flightUrl
    }
  }

  const getRukFlightUrl = (flight) => {
    const base_url = "https://www.ryanair.com/gb/en/trip/flights/select";

    const outbound_departure_date = dayjs.utc(flight.outboundDeparture).format('YYYY-MM-DD')


    if (flight.type === "return") {
      const inbound_departure_date = dayjs.utc(flight.inboundDeparture).format('YYYY-MM-DD')
      
      const flightUrl = `${base_url}?originIata=${flight.origin}&destinationIata=${flight.destination}&adults=${flight.numberOfPeople}&dateOut=${outbound_departure_date}&dateIn=${inbound_departure_date}&isReturn=true&teens=0&children=0&infants=0&isConnectedFlight=false&discount=0&promoCode=`

      return flightUrl
    } else {
      const flightUrl = `${base_url}?originIata=${flight.origin}&destinationIata=${flight.destination}&adults=${flight.numberOfPeople}&dateOut=${outbound_departure_date}&dateIn=&isReturn=false&teens=0&children=0&infants=0&isConnectedFlight=false&discount=0&promoCode=`
      
      return flightUrl
    }
  }

  const getIbeFlightUrl = (flight) => {
    const base_url = "https://www.iberia.com/flights/";

    const outbound_departure_date = dayjs.utc(flight.outboundDeparture).format('DD')
    const outbound_departure_month_year = dayjs.utc(flight.outboundDeparture).format('YYYYMM')
    const outbound_departure_year = dayjs.utc(flight.outboundDeparture).format('YYYY')

    if (flight.type === "return") {
      const inbound_departure_date = dayjs.utc(flight.inboundDeparture).format('DD')
      const inbound_departure_month_year = dayjs.utc(flight.inboundDeparture).format('YYYYMM')
      const inbound_departure_year = dayjs.utc(flight.inboundDeparture).format('YYYY')

      const flightUrl = `${base_url}?market=PT&language=pt&appliesOMB=false&splitEndCity=false&initializedOMB=true&flexible=true&TRIP_TYPE=2&BEGIN_CITY_01=${flight.origin}&END_CITY_01=${flight.destination}&BEGIN_DAY_01=${outbound_departure_date}&BEGIN_MONTH_01=${outbound_departure_month_year}&BEGIN_YEAR_01=${outbound_departure_year}&END_DAY_01=${inbound_departure_date}&END_MONTH_01=${inbound_departure_month_year}&END_YEAR_01=${inbound_departure_year}&FARE_TYPE=R&quadrigam=IBHMPA&ADT=${flight.numberOfPeople}&CHD=0&INF=0&BNN=0&YTH=0&YCD=0&residentCode=&familianumerosa=&BV_UseBVCookie=no&boton=Buscar&bookingMarket=PT`

      return flightUrl
    } else {
      const flightUrl = `${base_url}?market=PT&language=pt&appliesOMB=false&splitEndCity=false&initializedOMB=true&flexible=true&TRIP_TYPE=1&BEGIN_CITY_01=${flight.origin}&END_CITY_01=${flight.destination}&BEGIN_DAY_01=${outbound_departure_date}&BEGIN_MONTH_01=${outbound_departure_month_year}&FARE_TYPE=R&ADT=${flight.numberOfPeople}&CHD=0&INF=0&BNN=0&YTH=0&YCD=0&residentCode=&familianumerosa=&quadrigam=IBHMPA&BV_UseBVCookie=no&boton=Buscar&bookingMarket=ES`

      return flightUrl
    }
  }

  const getFlightUrl = (flight) => {
    const airline = flight.airline

    switch (airline) {
      case "TAP":
        return getTapFlightUrl(flight)
      case "TAM":
        return getTamFlightUrl(flight)
      case "RUK":
        return getRukFlightUrl(flight)
      case "IBE":
        return getIbeFlightUrl(flight)
      default:
        return "Unable to get URL..."
    }
  }

  const handleLinkClick = (flight) => {
    const flightUrl = getFlightUrl(flight)

    navigator.clipboard.writeText(flightUrl)
  }

  // Mobile card view for flights
  const renderMobileFlightCard = () => {
    return (
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
          <Typography variant="h6" component="div">
            Details:
          </Typography>
          <IconButton
            size="small"
            onClick={() => handleEdit(flightDetails.id)}
            color="primary"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleLinkClick(flightDetails)}
            color="primary"
          >
            <LinkIcon />
          </IconButton>
        </Box>
        
        <Table size="small">
          <TableBody>
          <TableRow sx={{
              '& td': {
                py: 2,               // More vertical space
                borderBottom: 'none' // No border between rows
              }
            }}>
              <TableCell>
                <Typography variant="subtitle2" color="text.secondary">Webhook for Alerts</Typography>
                <Typography variant="body2">{getWebhookName(flightDetails.webhookId)}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" color="text.secondary">Tracking Frequency</Typography>
                <Typography variant="body2">{flightDetails.trackingFrequency} minutes</Typography>
              </TableCell>
            </TableRow>
            <TableRow sx={{
              '& td': {
                py: 2,               // More vertical space
                borderBottom: 'none' // No border between rows
              }
            }}>
              <TableCell>
                <Typography variant="subtitle2" color="text.secondary">Trigger Price</Typography>
                <Typography variant="body2">{handleFlightCurrency(flightDetails.airline)} {flightDetails.minimumPrice}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" color="text.secondary">Last Tracked at</Typography>
                <Typography variant="body2">{flightDetails.lastTrackedAt ? formatLocalDate(flightDetails.lastTrackedAt) : "Never"}</Typography>
              </TableCell>
              
            </TableRow>
            <TableRow sx={{
              '& td': {
                py: 2,               // More vertical space
                borderBottom: 'none' // No border between rows
              }
            }}>
              <TableCell>
                <Typography variant="subtitle2" color="text.secondary">Airline</Typography>
                <Typography variant="body2">{flightDetails.airline}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" color="text.secondary">Origin</Typography>
                <Typography variant="body2">{flightDetails.origin}</Typography>
              </TableCell>
            </TableRow>
            <TableRow sx={{
              '& td': {
                py: 2,               // More vertical space
                borderBottom: 'none' // No border between rows
              }
            }}>
              <TableCell>
                <Typography variant="subtitle2" color="text.secondary">Destination</Typography>
                <Typography variant="body2">{flightDetails.destination}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" color="text.secondary">Number of People</Typography>
                <Typography variant="body2">{flightDetails.numberOfPeople}</Typography>
              </TableCell>
            </TableRow>
            <TableRow sx={{
              '& td': {
                py: 2,               // More vertical space
                borderBottom: 'none' // No border between rows
              }
            }}>
              <TableCell>
                <Typography variant="subtitle2" color="text.secondary">Departure to {flightDetails.destination}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FlightTakeoffIcon sx={{ mr: 0.5, color: 'primary.main', fontSize: 'inherit' }} />
                  <Typography variant="body2">{formatDate(flightDetails.outboundDeparture)}</Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" color="text.secondary">Arrival at {flightDetails.destination}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <FlightLandIcon sx={{ mr: 0.5, color: 'secondary.main', fontSize: 'inherit' }} />
                  <Typography variant="body2">{formatDate(flightDetails.outboundArrival)}</Typography>
                </Box>
              </TableCell>
              </TableRow>
              <TableRow sx={{
                '& td': {
                  py: 2,               // More vertical space
                  borderBottom: 'none' // No border between rows
                }
              }}>
              {flightDetails.type === "return" ? (
                <><TableCell>
                  <Typography variant="subtitle2" color="text.secondary">Return to {flightDetails.origin}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FlightTakeoffIcon sx={{ mr: 0.5, color: 'primary.main', fontSize: 'inherit', transform: 'scaleX(-1)' }} />
                    <Typography variant="body2">{formatDate(flightDetails.inboundDeparture)}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" color="text.secondary">Arrival at {flightDetails.origin}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <FlightLandIcon sx={{ mr: 0.5, color: 'secondary.main', fontSize: 'inherit', transform: 'scaleX(-1)' }} />
                    <Typography variant="body2">{formatDate(flightDetails.inboundArrival)}</Typography>
                  </Box>
                </TableCell></>
              ) : (
                <><TableCell>
                </TableCell>
                <TableCell>
                </TableCell></>
              )}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Desktop table view for flights
  const renderDesktopFlightTable = () => {
    return (
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
          <Typography variant="h6" component="div">
            Details:
          </Typography>
          <Box>
            <IconButton
              size="small"
              onClick={() => handleEdit(flightDetails.id)}
              color="primary"
            >
              <EditIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleLinkClick(flightDetails)}
              color="primary"
            >
              <LinkIcon />
            </IconButton>
          </Box>
        </Box>

        <Table size="small">
          <TableBody>
          <TableRow sx={{
              '& td': {
                py: 2,               // More vertical space
                borderBottom: 'none' // No border between rows
              }
            }}>
              <TableCell>
                <Typography variant="subtitle2" color="text.secondary">Webhook for Alerts</Typography>
                <Typography variant="body2">{getWebhookName(flightDetails.webhookId)}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" color="text.secondary">Tracking Frequency</Typography>
                <Typography variant="body2">{flightDetails.trackingFrequency} minutes</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" color="text.secondary">Trigger Price</Typography>
                <Typography variant="body2">{handleFlightCurrency(flightDetails.airline)} {flightDetails.minimumPrice}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" color="text.secondary">Last Tracked at</Typography>
                <Typography variant="body2">{flightDetails.lastTrackedAt ? formatLocalDate(flightDetails.lastTrackedAt) : "Never"}</Typography>
              </TableCell>
              
            </TableRow>
            <TableRow sx={{
              '& td': {
                py: 2,               // More vertical space
                borderBottom: 'none' // No border between rows
              }
            }}>
              <TableCell>
                <Typography variant="subtitle2" color="text.secondary">Airline</Typography>
                <Typography variant="body2">{flightDetails.airline}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" color="text.secondary">Origin</Typography>
                <Typography variant="body2">{flightDetails.origin}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" color="text.secondary">Destination</Typography>
                <Typography variant="body2">{flightDetails.destination}</Typography>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" color="text.secondary">Number of People</Typography>
                <Typography variant="body2">{flightDetails.numberOfPeople}</Typography>
              </TableCell>
            </TableRow>
            <TableRow sx={{
              '& td': {
                py: 2,               // More vertical space
                borderBottom: 'none' // No border between rows
              }
            }}>
              <TableCell>
                <Typography variant="subtitle2" color="text.secondary">Departure to {flightDetails.destination}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FlightTakeoffIcon sx={{ mr: 0.5, color: 'primary.main', fontSize: 'inherit' }} />
                  <Typography variant="body2">{formatDate(flightDetails.outboundDeparture)}</Typography>
                </Box>
              </TableCell>
              <TableCell>
                <Typography variant="subtitle2" color="text.secondary">Arrival at {flightDetails.destination}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <FlightLandIcon sx={{ mr: 0.5, color: 'secondary.main', fontSize: 'inherit' }} />
                  <Typography variant="body2">{formatDate(flightDetails.outboundArrival)}</Typography>
                </Box>
              </TableCell>
              {flightDetails.type === "return" ? (
                <><TableCell>
                  <Typography variant="subtitle2" color="text.secondary">Return to {flightDetails.origin}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FlightTakeoffIcon sx={{ mr: 0.5, color: 'primary.main', fontSize: 'inherit', transform: 'scaleX(-1)' }} />
                    <Typography variant="body2">{formatDate(flightDetails.inboundDeparture)}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" color="text.secondary">Arrival at {flightDetails.origin}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <FlightLandIcon sx={{ mr: 0.5, color: 'secondary.main', fontSize: 'inherit', transform: 'scaleX(-1)' }} />
                    <Typography variant="body2">{formatDate(flightDetails.inboundArrival)}</Typography>
                  </Box>
                </TableCell></>
              ) : (
                <><TableCell>
                </TableCell>
                <TableCell>
                </TableCell></>
              )}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 0, mb: 4, px: { xs: 2, sm: 3 } }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, width: '100%' }}>
          <CircularProgress />
        </Box>
      ) : !flightDetails ? (
        <Paper sx={{ p: 3, textAlign: 'center', width: '100%' }}>
          <Typography variant="h6" color="text.secondary">
            We didn't find your flight... please report it if you think it's a bug!
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ width: '100%', flex: 1 }}>
          <Typography variant="h4" gutterBottom sx={{ mb: isMobile ? 2 : 4, textAlign: 'center' }}>
            {flightDetails.name}
          </Typography>
          {isMobile ? renderMobileFlightCard() : renderDesktopFlightTable()}
        </Box>
      )}

      {loadingHistory ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, width: '100%' }}>
          <CircularProgress />
        </Box>
      ) : !flightDetails ? (
        <Paper sx={{ p: 3, textAlign: 'center', width: '100%' }}>
          <Typography variant="h6" color="text.secondary">
            We didn't find your flight... please report it if you think it's a bug!
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ width: '100%', flex: 1 }}>
          <PriceChart data={priceHistory} targetPrice={flightDetails.minimumPrice} currency={handleFlightCurrency(flightDetails.airline)} />
        </Box>
      )}
    </Container>
  );
};

export default TrackerDetails;